import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scalePoint } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import type { TFunction } from 'i18next';
import { Shield } from 'lucide-react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface DailyViews {
  date: string;
  count: number;
}

interface LinkWithViews {
  id: string;
  name?: string | null;
  projectName?: string | null;
  viewCount: number;
}

interface ProjectByLinks {
  projectId: string;
  projectName: string;
  linkCount: number;
}

interface SharedLinkStatsProps {
  dailyViews: DailyViews[];
  topProjectsByLinks: ProjectByLinks[];
  topLinksByViews: LinkWithViews[];
  totalLinks: number;
}

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const CHART_HEIGHT = 140;
const CHART_MARGIN = { top: 16, right: 12, bottom: 24, left: 12 };

const ViewsLineChart: FunctionComponent<{ width: number; dailyViews: DailyViews[]; t: TFunction }> = ({
  width,
  dailyViews,
  t,
}) => {
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<DailyViews>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ detectBounds: true, scroll: true });

  const innerWidth = width - CHART_MARGIN.left - CHART_MARGIN.right;
  const innerHeight = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;

  const xScale = useMemo(
    () =>
      scalePoint<string>({
        domain: dailyViews.map((d) => d.date),
        range: [0, innerWidth],
      }),
    [dailyViews, innerWidth],
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, Math.max(1, ...dailyViews.map((d) => d.count))],
        range: [innerHeight, 0],
        nice: true,
      }),
    [dailyViews, innerHeight],
  );

  const tickDates = useMemo(() => {
    if (dailyViews.length <= 7) return dailyViews.map((d) => d.date);
    const step = Math.ceil(dailyViews.length / 5);
    return dailyViews.filter((_, i) => i % step === 0 || i === dailyViews.length - 1).map((d) => d.date);
  }, [dailyViews]);

  const handleTooltip = useCallback(
    (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
      const point = localPoint(event);
      if (!point) return;
      const x0 = point.x - CHART_MARGIN.left;
      const step = innerWidth / Math.max(1, dailyViews.length - 1);
      const idx = Math.min(dailyViews.length - 1, Math.max(0, Math.round(x0 / step)));
      const d = dailyViews[idx];
      if (!d) return;
      showTooltip({
        tooltipData: d,
        tooltipLeft: (xScale(d.date) ?? 0) + CHART_MARGIN.left,
        tooltipTop: yScale(d.count) + CHART_MARGIN.top,
      });
    },
    [dailyViews, xScale, yScale, showTooltip, innerWidth],
  );

  if (width <= 0) return null;

  return (
    <div ref={containerRef} className='relative'>
      <svg width={width} height={CHART_HEIGHT}>
        <defs>
          <linearGradient id='area-gradient' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor='#6366f1' stopOpacity={0.2} />
            <stop offset='100%' stopColor='#6366f1' stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <Group top={CHART_MARGIN.top} left={CHART_MARGIN.left}>
          {yScale.ticks(3).map((tick) => (
            <line
              key={tick}
              x1={0}
              x2={innerWidth}
              y1={yScale(tick)}
              y2={yScale(tick)}
              className='stroke-slate-100 dark:stroke-stone-800'
              strokeDasharray='4,4'
            />
          ))}
          <AreaClosed
            data={dailyViews}
            x={(d) => xScale(d.date) ?? 0}
            y={(d) => yScale(d.count)}
            yScale={yScale}
            curve={curveMonotoneX}
            fill='url(#area-gradient)'
          />
          <LinePath
            data={dailyViews}
            x={(d) => xScale(d.date) ?? 0}
            y={(d) => yScale(d.count)}
            curve={curveMonotoneX}
            stroke='#6366f1'
            strokeWidth={2}
          />
          {tooltipData && (
            <circle
              cx={xScale(tooltipData.date) ?? 0}
              cy={yScale(tooltipData.count)}
              r={4}
              fill='#6366f1'
              stroke='#fff'
              strokeWidth={2}
            />
          )}
          {tickDates.map((date) => (
            <text
              key={date}
              x={xScale(date) ?? 0}
              y={innerHeight + 16}
              textAnchor='middle'
              className='fill-slate-400 dark:fill-stone-500 text-[10px] font-medium'>
              {formatDate(date)}
            </text>
          ))}
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill='transparent'
            onMouseMove={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseLeave={hideTooltip}
            onTouchEnd={hideTooltip}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          className='!bg-slate-800 !text-white !rounded-md !px-3 !py-2 !text-xs !shadow-lg'>
          <div className='font-semibold'>{formatDate(tooltipData.date)}</div>
          <div className='text-slate-300'>
            {t('dashboard.sharedLinks.viewCount' as any, { count: tooltipData.count } as any)}
          </div>
        </TooltipInPortal>
      )}
    </div>
  );
};

const SharedLinkStats: FunctionComponent<SharedLinkStatsProps> = ({
  dailyViews,
  topProjectsByLinks,
  topLinksByViews,
  totalLinks,
}) => {
  const { t } = useTranslation('common');
  const totalViews = useMemo(() => dailyViews.reduce((sum, d) => sum + d.count, 0), [dailyViews]);

  const hasData = totalLinks > 0 || dailyViews.some((d) => d.count > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-sm'>{t('dashboard.sharedLinks.title')}</CardTitle>
        <CardDescription>{t('dashboard.sharedLinks.description', { count: totalLinks })}</CardDescription>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <>
            {/* Line graph */}
            <div className='flex items-baseline gap-2 mb-3'>
              <span className='text-2xl font-bold dark:text-white'>{totalViews}</span>
              <span className='text-xs text-slate-500 dark:text-stone-400 font-medium'>
                {t('dashboard.sharedLinks.viewsLast30')}
              </span>
            </div>
            <ParentSize debounceTime={0}>
              {({ width: parentWidth }) => <ViewsLineChart width={parentWidth} dailyViews={dailyViews} t={t} />}
            </ParentSize>

            {/* Bottom: two columns */}
            <div className='grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100 dark:border-stone-800'>
              {/* Top projects by links */}
              <div>
                <div className='flex items-center gap-1.5 mb-2'>
                  <p className='text-xs text-slate-500 dark:text-stone-400 font-medium'>
                    {t('dashboard.sharedLinks.topProjects')}
                  </p>
                </div>
                {topProjectsByLinks.length > 0 ? (
                  <div className='space-y-1.5'>
                    {topProjectsByLinks.map((p, i) => (
                      <div key={p.projectId} className='flex items-center gap-2 text-xs'>
                        <span className='text-slate-400 dark:text-stone-500 w-3 text-right flex-shrink-0'>{i + 1}</span>
                        <span className='text-slate-600 dark:text-stone-300 truncate flex-1'>{p.projectName}</span>
                        <span className='text-slate-400 dark:text-stone-500 flex-shrink-0'>
                          {t('dashboard.sharedLinks.linkCount', { count: p.linkCount.toLocaleString() } as any)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='text-xs text-slate-400 dark:text-stone-500'>{t('dashboard.sharedLinks.noData')}</p>
                )}
              </div>

              {/* Top links by views */}
              <div>
                <div className='flex items-center gap-1.5 mb-2'>
                  <p className='text-xs text-slate-500 dark:text-stone-400 font-medium'>
                    {t('dashboard.sharedLinks.topLinks')}
                  </p>
                </div>
                {topLinksByViews.length > 0 ? (
                  <div className='space-y-1.5'>
                    {topLinksByViews.map((l, i) => (
                      <div key={l.id} className='flex items-center gap-2 text-xs'>
                        <span className='text-slate-400 dark:text-stone-500 w-3 text-right flex-shrink-0'>{i + 1}</span>
                        <div className='truncate flex-1'>
                          <span className='text-slate-600 dark:text-stone-300'>{l.name || l.projectName}</span>
                        </div>
                        <span className='text-slate-400 dark:text-stone-500 flex-shrink-0'>{l.viewCount}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='text-xs text-slate-400 dark:text-stone-500'>{t('dashboard.sharedLinks.noViews')}</p>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className='text-center py-6 text-sm text-slate-400 dark:text-stone-500'>
            <Shield className='size-8 mx-auto mb-2 opacity-50' />
            {t('dashboard.sharedLinks.noLinks')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SharedLinkStats;
